// @ts-strict-ignore
import { Operation } from '../../types';
import type { Change } from '../../types';
import type { Predicate } from '../../lib/predicatedProcessor';
import { extractActionTypeFromId, isPublicIdForActionType } from '../../../actions';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';

/**
 * These predicates are built using closures, so we need to build them prior to
 * attaching them to the main Predicates object
 */
const addOrModifyRuleChangesForOutcomes = buildRuleChangesPredicateForActionTypeAndOps(
  ActionType.OUTCOME, [Operation.Add, Operation.Modify],
);

const addOrModifyRuleChangesForTags = buildRuleChangesPredicateForActionTypeAndOps(
  ActionType.TAG, [Operation.Add, Operation.Modify],
);

const addOrModifyRuleChangesForServices = buildRuleChangesPredicateForActionTypeAndOps(
  ActionType.SERVICE, [Operation.Add, Operation.Modify],
);

const addOrModifyRuleChangesForOutputs = buildRuleChangesPredicateForActionTypeAndOps(
  ActionType.OUTPUT, [Operation.Add, Operation.Modify],
);

const addOrModifyRuleChangesForMatrices = buildRuleChangesPredicateForActionTypeAndOps(
  ActionType.MATRIX_MODEL, [Operation.Add, Operation.Modify],
);


/**
 * All predicates used by the Testing Suite formatter
 *
 * The values should always be Predicate<Change> (see below note on `satisfies`)
 */
const Predicates = Object.freeze({
  everything() {
    return true;
  },
  actionAdded({
    op,
    descriptor,
  }) {
    return op === Operation.Add && descriptor === 'action';
  },
  actionRemoved({
    op,
    descriptor,
  }) {
    return op === Operation.Remove && descriptor === 'action';
  },
  tagActionAddedOrRemoved(change) {
    return actionTypeAdded(ActionType.TAG)(change) || actionTypeRemoved(ActionType.TAG)(change);
  },
  outcomeActionAddedOrRemoved(change) {
    return (
      actionTypeAdded(ActionType.OUTCOME)(change) || actionTypeRemoved(ActionType.OUTCOME)(change)
    );
  },
  serviceActionAddedOrRemoved(change) {
    return (
      actionTypeAdded(ActionType.SERVICE)(change) || actionTypeRemoved(ActionType.SERVICE)(change)
    );
  },
  outputActionAddedOrRemoved(change) {
    return (
      actionTypeAdded(ActionType.OUTPUT)(change) || actionTypeRemoved(ActionType.OUTPUT)(change)
    );
  },
  matrixActionAddedOrRemoved(change) {
    return (
      actionTypeAdded(ActionType.MATRIX_MODEL)(change)
      || actionTypeRemoved(ActionType.MATRIX_MODEL)(change)
    );
  },
  outcomeMetadataModified({
    op,
    descriptor,
  }) {
    return op === Operation.Modify && descriptor === 'action.outcome.metadata';
  },
  inputMetadataModified({
    descriptor,
    op,
  }) {
    return op === Operation.Modify && descriptor === 'action.input.metadata';
  },
  outputMetadataModified({
    descriptor,
    op,
  }) {
    return op === Operation.Modify && descriptor === 'action.output.metadata';
  },
  outputExpressionModified({
    descriptor,
    op,
  }) {
    return op === Operation.Modify && descriptor === 'action.output.expression';
  },
  matrixMetadataModified({
    descriptor,
    op,
  }) {
    return op === Operation.Modify && descriptor === 'action.matrix.metadata';
  },
  /**
   * Predicate for tag global settings modified changes (e.g., tag swap)
   */
  tagGlobalSettingsModified({
    descriptor,
    op,
  }) {
    return op === Operation.Modify && descriptor === 'action.tag.globalSettings';
  },
  actionMLAAdded({
    descriptor,
    op,
  }) {
    return op === Operation.Add && descriptor === 'action.rules.mla';
  },
  addOrModifyRuleChangesForOutcomes,
  addOrModifyRuleChangesForOutputs,
  addOrModifyRuleChangesForServices,
  addOrModifyRuleChangesForTags,
  addOrModifyRuleChangesForMatrices,
}); // todo - add satisfies Record<string, Predicate<Change>>; when SWC supports it


/**
 * Returns a predicate function that evaluates to true when receiving any rule-related
 * change owned by an action of the specified type and operation(s).
 */
function buildRuleChangesPredicateForActionTypeAndOps(
  actionType: ActionType,
  operations: Operation[],
): Predicate<Change> {
  return ({ descriptor, actionPublicId, op }) => {
    if (!operations.includes(op)) {
      return false;
    }

    const isThresholdChangeForActionType = descriptor === 'threshold'
      && isPublicIdForActionType(actionPublicId, actionType);

    const isRuleGroupingChangeForActionType = op === Operation.Modify
      && descriptor === 'action.rules.grouping'
      && isPublicIdForActionType(actionPublicId, actionType);

    const isMLAChangeForActionType = (descriptor === 'action.rules.mla'
      && isPublicIdForActionType(actionPublicId, actionType));

    return isThresholdChangeForActionType
      || isRuleGroupingChangeForActionType
      || isMLAChangeForActionType;
  };
}


function actionTypeAdded(
  actionType: ActionType,
): Predicate<Change> {
  return change => (
    extractActionTypeFromId(change.actionPublicId) === actionType
    && Predicates.actionAdded(change)
  );
}

function actionTypeRemoved(
  actionType: ActionType,
): Predicate<Change> {
  return change => (
    extractActionTypeFromId(change.actionPublicId) === actionType
    && Predicates.actionRemoved(change)
  );
}


export default Predicates;
